import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/pages/auth/auth.context";
import { Loader } from "lucide-react";

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

export const RoleProtectedRoute = ({
  children,
  allowedRoles,
}: RoleProtectedRouteProps) => {
  const { token, user, loading } = useAuth();
  const location = useLocation();

  // Show loading indicator while auth state is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader className="h-8 w-8 animate-spin text-foreground" />
          <p className="text-foreground">Loading authentication...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    // Carry the destination the same way ProtectedRoute does, in ?redirect=,
    // because that is what SignIn reads. Router state was never read, so an
    // admin bounced to sign in landed on "/" instead of where they were going.
    const redirectTo = encodeURIComponent(
      location.pathname + location.search + location.hash,
    );
    return <Navigate to={`/signin?redirect=${redirectTo}`} replace />;
  }

  const hasRequiredRole = user?.roles?.some((role) =>
    allowedRoles.includes(role),
  );

  if (!hasRequiredRole) {
    // Redirect to unauthorized page or dashboard
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};
